import {
	Button,
	Chip,
	CircularProgress,
	Grid2,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "@dexteel/mesf-core";
import { useUTLSettingsContext } from "@dexteel/mesf-core";
import { ShiftData } from "@dexteel/mesf-core";
import { AssetTreePicker } from "@dexteel/mesf-core";
import { ShiftAutocomplete } from "@dexteel/mesf-core";
import { ShiftSelector } from "@dexteel/mesf-core";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { TimeService, getMomentTz } from "@dexteel/mesf-core";
import {
	AssetsFilter,
	ConfirmationDialogRaw,
	NotificationSnackBar,
} from "@/pages/logbook/_shared";
import { CreateWorkOrderFromLogbookEntry } from "@/shared/work-orders-modals/components/create-work-order-from-logbook-entry";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { getWorkOrdersBySourceEntry } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import type { DefaultShift } from "../../hooks/useCurrentShift";
import { useEntrySubmission } from "../../hooks/useEntrySubmission";
import { ENTRY_INITIAL_VALUES, Entry } from "../../models/Entry";
import { ContentInput } from "./ContentInput";

const moment = getMomentTz();

type Props = {
	entry: Entry | null;
	isNewEntry: boolean;
	show: "edit" | "view" | "";
	// shift a new entry starts on; also the reference for the "other shift" confirmation
	defaultShift: DefaultShift | null;
	onChangeShow: () => void;
	onHide: () => void;
	onSaved: (entry: Entry, shouldStayInEditMode?: boolean) => void;
	preselectedAssetId?: number | null;
	withAssetFilter?: "tree" | "selector";
	canEditAsset?: boolean;
	canEditShift?: boolean;
	filterAssets?: "area";
	allowAttachments?: boolean;
	shiftsRange?: number;
};

export const EntryViewer = ({
	entry,
	isNewEntry,
	show,
	defaultShift,
	onChangeShow,
	onHide,
	onSaved,
	preselectedAssetId,
	withAssetFilter,
	canEditAsset,
	canEditShift,
	filterAssets,
	allowAttachments,
	shiftsRange,
}: Props) => {
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const defaultAssetId = Number(get(settings, "DefaultAssetId", 15));

	const [notificationMessage, setNotificationMessage] = useState("");
	const [shiftTimestamp, setShiftTimestamp] = useState<Date | null>(null);
	const [error, setError] = useState("");

	const [linkedWorkOrders, setLinkedWorkOrders] = useState<WorkOrder[]>([]);
	const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(
		null,
	);

	const loadLinkedWorkOrders = useCallback(async () => {
		if (!entry?.EntryId) {
			setLinkedWorkOrders([]);
			return;
		}
		try {
			setLinkedWorkOrders(await getWorkOrdersBySourceEntry(entry.EntryId));
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, [entry?.EntryId]);

	useEffect(() => {
		loadLinkedWorkOrders();
	}, [loadLinkedWorkOrders]);

	const {
		control,
		handleSubmit,
		reset,
		formState: { isValid, dirtyFields },
		watch,
		setValue,
	} = useForm<Entry>();

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const updatedBy = watch("UpdatedBy");
	const updateTimestamp = watch("UpdateTimestamp");
	const createTimestamp = watch("CreateTimestamp");
	const content = watch("Content");
	const entryIdValue = watch("EntryId");
	const title = watch("Title");
	const author = watch("Author");
	const shiftId = watch("ShiftId");
	const assetId = watch("AssetId");
	const shift = watch("Shift");
	const crew = watch("Crew");

	// Handler for when entry is submitted via attachment modal
	const handleEntrySubmitted = (newEntryId: number, newEntryData?: any) => {
		setNotificationMessage("Entry created successfully");

		// If we have the new entry data, update the form immediately
		if (newEntryData) {
			reset(newEntryData);
		}

		onSaved(
			newEntryData ?? { ...ENTRY_INITIAL_VALUES, EntryId: newEntryId },
			true,
		);
	};

	// Kept while a save requested from "Create Work Order" is in flight, so that
	// button learns whether the entry was actually saved instead of waiting forever
	const pendingSaveResolve = useRef<((saved: boolean) => void) | null>(null);
	const resolvePendingSave = (saved: boolean) => {
		pendingSaveResolve.current?.(saved);
		pendingSaveResolve.current = null;
	};
	// the "other shift" confirmation closes the viewer before saving, so read this at submit time
	const submittedAsNewRef = useRef(false);

	// Use the shared entry submission hook
	const {
		submitEntry,
		isSubmitLoading,
		showConfirmationDialog,
		handleConfirmationClose,
	} = useEntrySubmission({
		onSuccess: (entry) => {
			resolvePendingSave(!!entry?.EntryId);
			setNotificationMessage(
				submittedAsNewRef.current
					? "Entry created successfully"
					: "Entry updated successfully",
			);
			// Ensure we have a valid EntryId before navigating
			if (entry && entry.EntryId) {
				onSaved(entry);
			} else {
				console.error("No EntryId received from API response", entry);
				setError("Failed to get entry ID from server response");
			}
		},
		onError: (error) => {
			resolvePendingSave(false);
			setError(error.message);
		},
		isNewEntry,
		onHide: () => onHide(),
	});

	const onSubmit = async (formData: Entry) => {
		setError("");
		submittedAsNewRef.current = isNewEntry;
		const submissionData = {
			assetId: formData.AssetId,
			entryId: formData.EntryId,
			shiftId: formData.ShiftId,
			isForOtherShift: entry?.ShiftId
				? entry.ShiftId !== formData.ShiftId
				: // with no shift running there is no default to differ from
					defaultShift?.ShiftId != null &&
					defaultShift.ShiftId !== formData.ShiftId,
			title: formData.Title,
			content: formData.Content,
			author: formData.Author ?? "",
			createTimestamp: isNewEntry ? new Date() : formData.CreateTimestamp!,
		};

		await submitEntry(submissionData);
	};

	// Save triggered by "Create Work Order": validation errors show on the fields
	// and resolve false, so the button does not stay loading forever
	const requestSaveForWorkOrder = () =>
		new Promise<boolean>((resolve) => {
			pendingSaveResolve.current = resolve;
			handleSubmit(onSubmit, () => resolvePendingSave(false))();
		});

	// tells "the new entry just started" from "a new entry is already being typed"
	const wasNewEntryRef = useRef(isNewEntry);

	useEffect(() => {
		const newEntryStarted = isNewEntry && !wasNewEntryRef.current;
		wasNewEntryRef.current = isNewEntry;
		// a new entry being typed keeps its text (and shift) when the page's default shift
		// moves, but starting one always begins from an empty form
		if (isNewEntry && isFormDirty && !newEntryStarted) return;
		const newEntry = {
			...ENTRY_INITIAL_VALUES,
			// there may be no shift running (none seeded yet): the user picks one
			ShiftId: defaultShift?.ShiftId!,
			Shift: defaultShift?.Shift ?? "",
			Crew: defaultShift?.Crew ?? "",
			CreateTimestamp: new Date(),
			AssetId: preselectedAssetId ?? defaultAssetId ?? null,
		};
		reset((isNewEntry ? newEntry : entry) || ENTRY_INITIAL_VALUES);
	}, [entry, defaultShift?.ShiftId, isNewEntry, reset, defaultAssetId]);

	useEffect(() => {
		if (isNewEntry) {
			setValue("AssetId", preselectedAssetId ?? defaultAssetId ?? null);
		}
	}, [preselectedAssetId, defaultAssetId]);

	return (
		<>
			{isNewEntry || entry?.EntryId ? (
				<HelmetDexteel
					title={`${
						isNewEntry ? "New Entry" : `'${entry?.Title}' by ${entry?.Author}`
					} - Logbook`}
				/>
			) : null}
			{isNewEntry || entry?.EntryId ? (
				<Paper
					elevation={1}
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Grid2
						container
						spacing={1}
						component={"form"}
						onSubmit={handleSubmit(onSubmit)}
						height={"100%"}
						flexDirection={"column"}
						flexWrap={"nowrap"}
						p={1}
					>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Title"
								control={control}
								rules={{
									required: "Title is required",
									validate: (value) =>
										!!value?.trim() || "Title cannot be empty",
								}}
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<TextField
										label="Title"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
										autoFocus={isNewEntry}
										size="medium"
									/>
								)}
							/>
						</Grid2>
						<Grid2 container size={{ xs: 12 }} spacing={2}>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<Controller
									name="CreateTimestamp"
									control={control}
									render={({ field: { value } }) => (
										<DateTimePicker
											label="Creation Time"
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm"
											value={value ? moment(value) : null}
											disabled
											slotProps={{
												textField: {
													variant: "outlined",
													size: "small",
													fullWidth: true,
													InputLabelProps: {
														shrink: true,
														style: {
															fontSize: ".875rem",
															marginTop: -2,
														},
													},
												},
											}}
										/>
									)}
								/>
							</Grid2>
							{canEditShift ? (
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Controller
										name="ShiftId"
										control={control}
										render={({ field }) => (
											<ShiftAutocomplete
												// the list is built around this shift, so it must contain the entry's own
												currentShiftId={
													(isNewEntry ? undefined : entry?.ShiftId) ??
													defaultShift?.ShiftId ??
													0
												}
												onShiftSelected={(selectedShift: ShiftData | null) => {
													if (selectedShift) {
														setValue("Shift", selectedShift.Shift, {
															shouldDirty: true,
														});
														setValue("Crew", selectedShift.Crew, {
															shouldDirty: true,
														});
														setShiftTimestamp(new Date(selectedShift.Start));
														field.onChange(selectedShift.ShiftId);
													} else {
														setValue("Shift", null, { shouldDirty: true });
														setValue("Crew", null, { shouldDirty: true });
														setShiftTimestamp(null);
														field.onChange(null);
													}
												}}
												value={field.value as number | null}
												disabled={!isNewEntry && show !== "edit"}
												shiftsRange={shiftsRange}
											/>
										)}
									/>
								</Grid2>
							) : (
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Controller
										name="ShiftId"
										control={control}
										render={({ field }) => (
											<ShiftSelector
												onChange={field.onChange}
												value={field.value as number | null}
												disabled
											/>
										)}
									/>
								</Grid2>
							)}
							<Grid2 size={{ xs: 12, md: canEditAsset ? 2 : 6 }}>
								<Controller
									name="Author"
									control={control}
									rules={{
										required: "Author is required",
									}}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextField
											label="Author"
											variant="outlined"
											value={value ?? ""}
											onChange={onChange}
											fullWidth
											error={!!error}
											helperText={error?.message}
											disabled={!isNewEntry}
											size="small"
										/>
									)}
								/>
							</Grid2>
							{canEditAsset ? (
								withAssetFilter === "tree" ? (
									<Grid2 size={{ md: 4, xs: 12 }}>
										<Controller
											name="AssetId"
											control={control}
											render={({ field }) => (
												<AssetTreePicker
													label="Asset"
													value={field.value || 0}
													onChange={field.onChange}
												/>
											)}
										/>
									</Grid2>
								) : withAssetFilter === "selector" ? (
									<Grid2 size={{ md: 4, xs: 12 }}>
										<Controller
											name="AssetId"
											control={control}
											render={({ field }) => (
												<AssetsFilter
													value={field.value as number | null}
													onChange={field.onChange}
													filterAreaAssets={filterAssets === "area"}
												/>
											)}
										/>
									</Grid2>
								) : null
							) : null}
						</Grid2>
						<Grid2
							size={{ md: 12 }}
							height={"100%"}
							flexGrow={1}
							overflow="auto"
						>
							<Controller
								name="Content"
								control={control}
								render={({ field: { value, onChange } }) => (
									<ContentInput
										UpdatedBy={updatedBy as string}
										UpdateTimestamp={updateTimestamp as Date}
										onChange={(val) => onChange(val)}
										value={value as string}
										isEditing={show === "edit"}
										isNewEntry={isNewEntry}
										entryId={entryIdValue as number}
										entryData={{
											assetId: assetId as number,
											entryId: entryIdValue as number,
											shiftId: shiftId as number,
											title: title as string,
											content: value as string,
											author: author as string,
											createTimestamp: isNewEntry
												? new Date()
												: (createTimestamp as Date),
										}}
										onEntrySubmitted={handleEntrySubmitted}
										allowAttachments={allowAttachments}
									/>
								)}
							/>
						</Grid2>
						{linkedWorkOrders.length > 0 && (
							<Grid2
								container
								size={{ xs: 12 }}
								spacing={1}
								alignItems="center"
								sx={{ mb: 1 }}
							>
								<Typography
									variant="caption"
									sx={{ mr: 0, fontWeight: "bold", color: "#0000008A" }}
								>
									Linked Work Orders:
								</Typography>
								{linkedWorkOrders.map((wo) => (
									<Chip
										key={wo.WorkOrderId}
										label={wo.WorkOrderName}
										size="small"
										variant="outlined"
										color={
											wo.WorkOrderStatusCode === "O" ? "primary" : "default"
										}
										onClick={() => setSelectedWorkOrder(wo)}
										sx={{ cursor: "pointer" }}
									/>
								))}
							</Grid2>
						)}
						<Grid2 container size={{ xs: 12 }}>
							<Grid2 container size={{ md: 6, xs: 12 }} spacing={1}>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => onHide()}
									>
										Close
									</Button>
								</Grid2>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => onChangeShow()}
									>
										{show === "edit" ? "Preview" : "Edit"}
									</Button>
								</Grid2>
							</Grid2>
							<Grid2
								container
								size={{ md: 6, xs: 12 }}
								style={{ display: "flex", justifyContent: "flex-end" }}
							>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<CreateWorkOrderFromLogbookEntry
										entry={entry ?? ENTRY_INITIAL_VALUES}
										isNewEntry={isNewEntry}
										requestSave={requestSaveForWorkOrder}
										onClosed={loadLinkedWorkOrders}
									/>
								</Grid2>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading || !isFormDirty}
										variant="contained"
										color="primary"
										type="submit"
									>
										{isNewEntry ? "Create" : "Save"}
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</Paper>
			) : null}
			<EditWorkOrderModal
				show={selectedWorkOrder !== null}
				hideSourceEntryLink
				data={selectedWorkOrder as unknown as ModalWorkOrder}
				onHide={(shouldUpdate) => {
					setSelectedWorkOrder(null);
					if (shouldUpdate) loadLinkedWorkOrders();
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ConfirmationDialogRaw
				open={showConfirmationDialog}
				onClose={(value) => {
					if (value !== "OK") resolvePendingSave(false);
					handleConfirmationClose(value);
				}}
				title="Shift Submission Confirmation"
				message={`You are about to ${
					isNewEntry ? "create" : "save"
				} an entry for ${moment(shiftTimestamp).format(
					"MM/DD/YYYY",
				)} ${shift} - ${crew}. Are you sure you want to proceed?`}
				suffixTitle="Logbook Entry"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
